import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { Close, UploadFile } from '@mui/icons-material'
import { DatePicker } from '@impres-development/assets/client'
import { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import dayjs from 'dayjs'
import { ComplianceStatus, quoteLineType, supplierQuoteImportStateType } from '../../types'
import { SupplierSearchAutocomplete } from '../../helper'
import ScrmWarningDialog from './ScrmWarningDialog'
import './SupplierQuoteImport.css'

export default function ImportSupplierQuoteTemplateDialog({
  open,
  onClose,
  quoteId,
}: {
  open: boolean
  onClose: () => void
  quoteId: string
}) {
  const navigate = useNavigate()
  const [file, setFile] = useState<File | null>(null)
  const [provider, setProvider] = useState<string | null>(null)
  const [quoteNumber, setQuoteNumber] = useState('')
  const [expirationDate, setExpirationDate] = useState<Date | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingImportState, setPendingImportState] = useState<supplierQuoteImportStateType | null>(null)

  const reset = () => {
    setFile(null)
    setProvider(null)
    setQuoteNumber('')
    setExpirationDate(null)
    setSubmitting(false)
    setError(null)
  }

  const {
    getRootProps,
    getInputProps,
    open: chooseFiles,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxFiles: 1,
    onDropAccepted: accepted => {
      setFile(accepted[0])
      setError(null)
    },
  })

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleSubmit = () => {
    if (!file || !provider || !quoteNumber || !expirationDate) return

    setSubmitting(true)
    setError(null)

    const formData = new FormData()
    formData.append('file', file)

    axios
      .post(`/quote/${quoteId}/vendorTemplate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(async res => {
        const lineItems = res.data.lineItems as quoteLineType[]
        if (lineItems.length === 0) {
          setError(
            'No line items found. Every row needs #, Supplier, Manufacturer, Mfr Part Number, Display Part Number and Description filled in.',
          )
          setSubmitting(false)
          return
        }

        const { data: scrmResults } = await axios.post<{ LineNumber: number; Scrm: ComplianceStatus }[]>(
          `/quote/${quoteId}/checkScrmCompliance`,
          { lineItems },
        )
        const scrmByLine = new Map<number, ComplianceStatus>(scrmResults.map(r => [r.LineNumber, r.Scrm]))
        const lineItemsWithScrm = lineItems.map(item => ({ ...item, Scrm: scrmByLine.get(item.LineNumber) ?? null }))

        const totalAmount = lineItemsWithScrm.reduce((total, lineItem) => total + (lineItem.ExtCost ?? 0), 0)
        const state: supplierQuoteImportStateType = {
          quoteId,
          lineItems: lineItemsWithScrm,
          quoteNumber,
          supplier: provider,
          totalAmount,
          expirationDate,
          file,
        }

        const hasNonCompliantLine = lineItemsWithScrm.some(item => item.Scrm === ComplianceStatus.NotCompliant)
        if (hasNonCompliantLine) {
          setSubmitting(false)
          setPendingImportState(state)
          return
        }

        reset()
        onClose()
        navigate(`/quote/${quoteId}/validation`, { state })
      })
      .catch(err => {
        console.error(err)
        setError(err.response?.data?.message ?? 'Failed to import the template. Double check the file and try again.')
        setSubmitting(false)
      })
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle variant="h6">Import Supplier Quote Template</DialogTitle>
      <DialogContent className="import-supplier-quote-dialog-content">
        <SupplierSearchAutocomplete value={provider} onChange={setProvider} label="Supplier Name" fullWidth />
        <TextField
          fullWidth
          size="small"
          label="Supplier Quote #"
          value={quoteNumber}
          onChange={e => setQuoteNumber(e.target.value)}
        />
        <DatePicker
          fullWidth
          size="small"
          label="Expiration Date"
          value={expirationDate ? dayjs(expirationDate) : null}
          onChange={value => setExpirationDate(value?.toDate() ?? null)}
        />{' '}
        <div {...getRootProps()} className="import-supplier-quote-dropzone">
          <input {...getInputProps()} />
          <UploadFile color="disabled" />
          <Typography>Drop Files to Upload</Typography>
          <Typography color="text.secondary">or</Typography>
          <Button variant="contained" onClick={chooseFiles}>
            Choose Files
          </Button>
        </div>
        {file && (
          <div className="import-supplier-quote-file-card">
            <Box flex={1}>
              <Typography variant="body2">{file.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {(file.size / 1024 / 1024).toFixed(1)} MB
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setFile(null)} aria-label="Remove file">
              <Close fontSize="small" />
            </IconButton>
          </div>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!file || !provider || !quoteNumber || !expirationDate}
          loading={submitting}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </DialogActions>
      <ScrmWarningDialog
        lineItems={pendingImportState?.lineItems ?? null}
        onClose={() => setPendingImportState(null)}
        onImport={() => {
          if (!pendingImportState) return
          reset()
          onClose()
          navigate(`/quote/${quoteId}/validation`, { state: pendingImportState })
        }}
      />
    </Dialog>
  )
}
